/**
 * Design System — Suvidha AI (Pharmacy)
 * Warm-paper canvas, ink navy + gold/spark accents, soft tactile cards, Hanken Grotesk + Mukta type,
 * IBM Plex Mono for money/codes/batch numbers, bilingual Hindi-primary labels.
 * Phosphor line icons (`<i className="ph ph-…">`), self-hosted via @fontsource in `src/index.js`.
 * Import { T, S, Badge, Toast, EmptyState, PageHeader, Modal, Toggle, useIsNarrow, rupee } from '../styles/ds';
 *
 * This file is the ONLY place a colour, radius, font or money format is defined. Pages must not
 * hand-roll a local `const T = {...}` / `const s = {...}` block — see the "Frontend UI & Copy
 * Standard" in AGENTS.md.
 */

// ── tokens ───────────────────────────────────────────────────────────────────
export const T = {
  // surfaces
  bg:      '#F4F1EA',   // paper — app canvas
  bg2:     '#FBFAF7',   // paper-2 — inset rows, subtle fills
  bg3:     '#EBE5DB',   // behind-canvas / body
  surface: '#FFFFFF',   // card
  border:  '#EBE7DE',   // hairline card border
  border2: '#F3EFE8',   // inner dividers / table row lines
  // text / brand
  ink:     '#15293F',   // primary navy
  pri:     '#15293F',   // alias (existing consumers) = ink
  ink2:    '#1B2A3A',   // body text on paper
  sec:     '#5A6472',   // secondary text
  // Faint labels / meta. Darker than the POS palette's #98A0AC on purpose: that value renders at
  // 2.3:1 on paper, which fails WCAG AA (4.5:1) at every text size. This passes at 4.6:1 on paper /
  // 5.2:1 on white while still reading a step lighter than `sec`. Do not lighten it back without
  // re-checking contrast — `mute` carries real instruction text, not just decoration.
  mute:    '#666E7A',
  gold:    '#B67A2E',   // accent gold ("AI" on light, active marker)
  spark:   '#E7B36A',   // spark accent on dark
  spark2:  '#D89A4A',   // deeper spark (icons/hover)
  accent:  '#15293F',   // alias = ink
  whatsapp:'#25995A',
  // type
  // Mukta carries the Devanagari coverage. `Noto Sans Devanagari` trails it as a system fallback:
  // if the bundled font fails to load, Hindi copy — which is most of this app's copy — would
  // otherwise drop to generic sans-serif and render poorly on some Android/WebView skins.
  font:    "'Hanken Grotesk','Mukta','Noto Sans Devanagari',sans-serif",
  fontHi:  "'Mukta','Noto Sans Devanagari','Hanken Grotesk',sans-serif",   // Hindi-leaning stack
  mono:    "'IBM Plex Mono',monospace",
  // radii
  r:       '16px',   // cards
  rs:      '12px',   // buttons / inputs
  rchip:   '9999px', // pills
  // gradients
  inkGrad:     'linear-gradient(135deg,#1A3350,#0E1F30)',
  heroGrad:    'linear-gradient(140deg,#1A3350 0%,#0F2033 100%)',
  sidebarGrad: 'linear-gradient(180deg,#173049 0%,#112436 100%)',
  // shadows
  shadow:   '0 1px 2px rgba(20,30,45,0.04)',
  shadowLg: '0 1px 2px rgba(20,30,45,0.04), 0 24px 44px -34px rgba(20,30,45,0.32)',
  shadowBtn:'0 12px 26px -12px rgba(21,41,63,0.65)',
  shadowHero:'0 20px 40px -28px rgba(21,41,63,0.6)',
  // semantic (design tones)
  green:   { bg: '#EAF3EC', text: '#2E7A54', border: '#D2E5D6' },
  red:     { bg: '#F8E9E7', text: '#B03A3A', border: '#EDD2CD' },
  yellow:  { bg: '#F7EFD9', text: '#9A6B1C', border: '#E9DBB6' },
  blue:    { bg: '#E1F3FE', text: '#1F6C9F', border: '#B3D9F5' },
  orange:  { bg: '#FBEEDD', text: '#B5610F', border: '#EFD8B4' },
  gold_t:  { bg: '#F6ECDD', text: '#B4732E', border: '#E4DCCC' },
  // Table-scroll helpers — also in S; mirrored here so a page that imports only `T` can reach them.
  tableScroll:   { overflow: 'auto', maxHeight: 'calc(100dvh - 220px)', WebkitOverflowScrolling: 'touch' },
  stickyColHead: { position: 'sticky', left: 0, zIndex: 3, background: '#FBFAF7' },   // = T.bg2
  stickyColCell: { position: 'sticky', left: 0, zIndex: 1, background: '#FFFFFF' },   // = T.surface
};

// `amber` is what the four local-token pages call this tone; alias it so `<Badge variant="amber">`
// and a mechanical local-block removal both keep working. `yellow` stays the canonical name.
T.amber = T.yellow;

// ── shared style objects ──────────────────────────────────────────────────────
export const S = {
  page:      { minHeight: '100vh', background: T.bg, fontFamily: T.font, padding: '28px 20px', color: T.pri },
  wrap:      { maxWidth: 900, margin: '0 auto' },
  card:      { background: T.surface, border: `1px solid ${T.border}`, borderRadius: T.r, overflow: 'hidden', boxShadow: T.shadow },
  cardPad:   { background: T.surface, border: `1px solid ${T.border}`, borderRadius: T.r, padding: '20px 24px', boxShadow: T.shadow },
  input:     { width: '100%', padding: '8px 12px', border: `1px solid ${T.border}`, borderRadius: T.rs, fontSize: 13, color: T.pri, background: T.surface, outline: 'none', boxSizing: 'border-box', fontFamily: T.font, minHeight: 44 },
  inputBg:   { width: '100%', padding: '8px 12px', border: `1px solid ${T.border}`, borderRadius: T.rs, fontSize: 13, color: T.pri, background: T.bg, outline: 'none', boxSizing: 'border-box', fontFamily: T.font, minHeight: 44 },
  btnPri:    { padding: '9px 18px', background: T.ink, color: '#fff', border: 'none', borderRadius: T.rs, fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: T.font, whiteSpace: 'nowrap', boxShadow: T.shadowBtn, minHeight: 44 },
  btnSec:    { padding: '8px 16px', background: 'transparent', color: T.sec, border: `1px solid ${T.border}`, borderRadius: T.rs, fontSize: 13, cursor: 'pointer', fontFamily: T.font, whiteSpace: 'nowrap', minHeight: 44 },
  btnDanger: { padding: '8px 16px', background: T.red.bg, color: T.red.text, border: `1px solid ${T.red.border}`, borderRadius: T.rs, fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: T.font, minHeight: 44 },
  btnSuccess:{ padding: '8px 16px', background: T.green.bg, color: T.green.text, border: `1px solid ${T.green.border}`, borderRadius: T.rs, fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: T.font, minHeight: 44 },
  // Button shape with NO fill — for controls that supply their own background/border (tab pills,
  // per-status row actions). Prefer btnPri/btnSec/btnDanger/btnSuccess whenever one of them fits.
  btnBase:   { padding: '9px 16px', border: 'none', borderRadius: T.rs, fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: T.font, minHeight: 44 },
  label:     { display: 'block', fontSize: 11, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: T.sec, marginBottom: 5 },
  th:        { padding: '10px 16px', fontSize: 11, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: T.sec, textAlign: 'left', background: T.bg2, borderBottom: `1px solid ${T.border}` },
  td:        { padding: '12px 16px', fontSize: 13, color: T.pri, borderBottom: `1px solid ${T.border}` },
  // Data-table scroll pane: bounded height so BOTH scrollbars stay on-screen (no scroll-to-the-bottom
  // -then-sideways). Pair with `thSticky` on the header cells.
  tableScroll:   { overflow: 'auto', maxHeight: 'calc(100dvh - 220px)', WebkitOverflowScrolling: 'touch' },
  // Header that stays visible while the rows scroll. Opt-in (not folded into `th`) because a page
  // whose table has no `tableScroll` pane would otherwise start sticking headers to the page scroll.
  thSticky:  { padding: '10px 16px', fontSize: 11, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: T.sec, textAlign: 'left', background: T.bg2, borderBottom: `1px solid ${T.border}`, position: 'sticky', top: 0, zIndex: 2 },
  // Pin the first column while scrolling sideways: opaque bg so scrolled cells don't bleed through.
  stickyColHead: { position: 'sticky', left: 0, zIndex: 3, background: T.bg2 },      // first header cell (corner)
  stickyColCell: { position: 'sticky', left: 0, zIndex: 1, background: T.surface },  // first body cell in each row
  // Responsive list cards — render these below `useIsNarrow()` in place of a wide table.
  cardRow:   { background: T.surface, border: `1px solid ${T.border}`, borderRadius: T.r, padding: '12px 14px', marginBottom: 10, display: 'flex', flexDirection: 'column', gap: 7, WebkitTapHighlightColor: 'transparent', boxShadow: T.shadow },
  cardCell:  { display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12, fontSize: 13, color: T.pri, minWidth: 0 },
  cardLabel: { fontSize: 10, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: T.sec, flexShrink: 0 },
  cardActions:{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 4 },
  // Status pill base — set `background`/`color` from a semantic tone (e.g. `...T.green`).
  pill:      { display: 'inline-block', fontSize: 10, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', padding: '2px 7px', borderRadius: 9999, whiteSpace: 'nowrap', fontFamily: T.font },
  // A label that holds a short SENTENCE (a warning, a note). `pill` above is for a 2-3 letter code: a sentence
  // in 10px capitals is hard to read, and its nowrap clips on a 375px phone. Set `background`/`color` from a tone.
  pillText:  { display: 'inline-block', fontSize: 12, fontWeight: 600, lineHeight: 1.35, padding: '3px 8px', borderRadius: T.rs, whiteSpace: 'normal', fontFamily: T.font },
  // Spread onto any tappable control to guarantee a ≥44px hit area (WCAG 2.5.5).
  tap:       { minHeight: 44, minWidth: 44, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' },
  h1:        { margin: 0, fontSize: 22, fontWeight: 700, color: T.pri, letterSpacing: '-0.02em', fontFamily: T.font },
  h2:        { margin: 0, fontSize: 17, fontWeight: 700, color: T.pri, letterSpacing: '-0.01em', fontFamily: T.font },
  pageHint:  { margin: '4px 0 0', fontSize: 13, color: T.sec, fontFamily: T.font },
  divider:   { borderTop: `1px solid ${T.border}`, margin: '16px 0' },
  // A failed or malformed load — never the same state as a genuine empty result. Pair with
  // `role="alert"` and a Retry action; never style an error state ad hoc, so it always reads as
  // unmistakably distinct from `EmptyState`'s calm, muted look.
  alertBox:    { padding: '14px 16px', background: T.red.bg, border: `2px solid ${T.red.border}`, borderRadius: T.rs, fontFamily: T.font },
  alertTitle:  { fontSize: 13, fontWeight: 700, color: T.red.text, marginBottom: 6 },
  alertDetail: { fontSize: 12, color: T.red.text, marginBottom: 10 },
  // Shared header/success-panel typography for the pre-login "account link" screens
  // AccountResetPassword, AccountForgotPassword and AccountVerifyEmail. NOT AccountSignIn: its
  // header (the shop name) is deliberately larger — 30px, not this token's 22px — and it has no
  // green success panel at all (a successful sign-in just reloads the page). Review finding, real:
  // do not retrofit `screenTitle` onto AccountSignIn's header, that would silently shrink it.
  screenTitle:    { fontSize: 22, fontWeight: 800, color: T.ink, letterSpacing: -0.5 },
  screenSubtitle: { fontSize: 13, color: T.sec, marginTop: 4 },
  screenStatus:   { textAlign: 'center', padding: '14px 16px', fontSize: 13, color: T.sec },
  successEmoji:   { fontSize: 22, marginBottom: 6 },
  successTitle:   { fontSize: 13, fontWeight: 700, color: T.green.text, marginBottom: 6 },
  successDetail:  { fontSize: 12, color: T.sec },
  // PH-50 — the "Switch shop" chip and the rows of its picker. The chip sits on a light header
  // (`switchChip`) and on the dark sidebar/drawer (`switchChipDark`), so both tones live here
  // rather than being hand-rolled in the component.
  switchChip:     { marginTop: 6, padding: '4px 10px', borderRadius: T.rchip, cursor: 'pointer', fontSize: 12, fontWeight: 600, fontFamily: T.font, background: T.surface, color: T.ink, border: `1px solid ${T.border}` },
  switchChipDark: { marginTop: 6, padding: '4px 10px', borderRadius: T.rchip, cursor: 'pointer', fontSize: 12, fontWeight: 600, fontFamily: T.font, background: 'rgba(255,255,255,0.08)', color: '#fff', border: '1px solid rgba(255,255,255,0.25)' },
  switchRowName:  { fontSize: 14, fontWeight: 700 },
  switchRowSlug:  { fontSize: 11, color: T.mute, fontFamily: T.mono },
};

// ── formatters ────────────────────────────────────────────────────────────────
// One money formatter for the whole app. `decimals` defaults to 0 (whole rupees — reports, KPIs,
// P&L); pass 2 where paise are load-bearing (scheme accruals, credit notes, order lines).
// Never hand-roll `₹${n.toFixed(2)}` — it renders 1234.50 instead of ₹1,234.50 in en-IN grouping.
export const rupee = (n, decimals = 0) =>
  `₹${Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}`;

// Paise-precision money — scheme accruals, credit notes, order lines, anything that reconciles
// against a supplier document. Whole-rupee `rupee()` is the default everywhere else.
export const rupee2 = (n) => rupee(n, 2);

// Bare number in en-IN grouping (quantities, ratios) — no currency symbol.
export const num = (n, maxFrac = 4) =>
  Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: maxFrac });

// ISO date → "05 Aug 26". Returns '' for null/undefined, echoes the input if unparseable.
export const fmtDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return isNaN(d) ? iso : d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' });
};

// Global :focus-visible ring (keyboard a11y). Inline styles can't express :focus-visible, and
// S.input zeroes `outline`, so we inject one stylesheet once. The box-shadow shows even where an
// inline `outline:none` is set; the outline covers controls that don't set one.
if (typeof document !== 'undefined' && !document.getElementById('ds-focus-style')) {
  const _fs = document.createElement('style');
  _fs.id = 'ds-focus-style';
  const FOCUSABLE_SEL =
    'button:focus-visible,a:focus-visible,input:focus-visible,select:focus-visible,' +
    'textarea:focus-visible,[role="switch"]:focus-visible,[role="button"]:focus-visible,' +
    '[tabindex]:focus-visible';
  _fs.textContent =
    FOCUSABLE_SEL + '{outline:2px solid ' + T.pri + ';outline-offset:2px;' +
    'box-shadow:0 0 0 3px rgba(21,41,63,0.30);}' +
    // On the ink sidebar / mobile nav the navy ring lands at ~1.1:1 — invisible. Any container
    // painted with an ink surface must carry `data-surface="dark"` so its controls get the spark
    // ring instead (7.1:1 on the lighter gradient stop, 8.3:1 on the darker).
    '[data-surface="dark"] ' + FOCUSABLE_SEL.split(',').join(',[data-surface="dark"] ') +
    '{outline-color:' + T.spark + ';box-shadow:0 0 0 3px rgba(231,179,106,0.35);}';
  document.head.appendChild(_fs);
}

import React, { useState, useEffect, useRef } from 'react';

// ── responsive hook ───────────────────────────────────────────────────────────
// True when the viewport is narrower than `breakpoint` — used by list pages to swap a wide
// horizontal-scroll <table> for the stacked `S.cardRow` layout. Default 768px (phone/portrait).
export function useIsNarrow(breakpoint = 768) {
  const [isNarrow, setIsNarrow] = useState(
    () => (typeof window !== 'undefined' ? window.innerWidth < breakpoint : false)
  );
  useEffect(() => {
    const onResize = () => setIsNarrow(window.innerWidth < breakpoint);
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [breakpoint]);
  return isNarrow;
}

// ── reusable on/off switch ─────────────────────────────────────────────────────
// 44×24 track inside a ≥44px hit area (WCAG 2.5.5). `color` overrides the "on" tint.
export function Toggle({ on, onChange, disabled = false, color, label }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={!!on}
      aria-label={label}
      disabled={disabled}
      onClick={onChange}
      style={{
        ...S.tap, width: 52, height: 44, padding: 0, background: 'none', border: 'none',
        cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.6 : 1, flexShrink: 0,
      }}
    >
      <span style={{ position: 'relative', display: 'inline-block', width: 44, height: 24, borderRadius: 12, background: on ? (color || T.green.text) : T.mute, transition: 'background 0.2s' }}>
        <span style={{ position: 'absolute', top: 3, left: on ? 23 : 3, width: 18, height: 18, borderRadius: '50%', background: T.surface, transition: 'left 0.2s', display: 'block' }} />
      </span>
    </button>
  );
}

// ── reusable badge ────────────────────────────────────────────────────────────
export function Badge({ variant = 'green', children, small }) {
  const c = T[variant] || T.green;
  return (
    <span style={{
      display: 'inline-block',
      padding: small ? '1px 7px' : '2px 9px',
      borderRadius: 9999,
      fontSize: small ? 9 : 10,
      fontWeight: 700,
      letterSpacing: '0.06em',
      textTransform: 'uppercase',
      background: c.bg,
      color: c.text,
      fontFamily: T.font,
    }}>
      {children}
    </span>
  );
}

export function Toast({ message, variant = 'green', onDismiss }) {
  if (!message) return null;
  const c = T[variant] || T.green;
  return (
    <div role="status" aria-live="polite" style={{ background: c.bg, color: c.text, fontSize: 13, fontWeight: 600, padding: '8px 8px 8px 14px', borderRadius: T.r, marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontFamily: T.font }}>
      <span>{message}</span>
      {onDismiss && <button onClick={onDismiss} aria-label="बंद करें / Dismiss" style={{ background: 'none', border: 'none', color: c.text, cursor: 'pointer', fontSize: 18, lineHeight: 1, marginLeft: 10, minWidth: 44, minHeight: 44, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>×</button>}
    </div>
  );
}

export function EmptyState({ icon, title, subtitle, action, onAction }) {
  return (
    <div style={{ padding: '52px 0', textAlign: 'center', fontFamily: T.font }}>
      {icon && <div style={{ fontSize: 28, marginBottom: 12, opacity: 0.3 }}>{icon}</div>}
      <div style={{ fontSize: 14, fontWeight: 600, color: T.sec, marginBottom: 4 }}>{title}</div>
      {subtitle && <div style={{ fontSize: 12, color: T.mute, marginBottom: 16 }}>{subtitle}</div>}
      {action && onAction && (
        <button onClick={onAction} style={{ ...S.btnPri, fontSize: 12 }}>{action}</button>
      )}
    </div>
  );
}

export function PageHeader({ title, subtitle, action, onAction, actionStyle }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24, fontFamily: T.font }}>
      <div>
        <h2 style={S.h1}>{title}</h2>
        {subtitle && <p style={S.pageHint}>{subtitle}</p>}
      </div>
      {action && onAction && (
        <button onClick={onAction} style={{ ...(actionStyle || S.btnPri), minHeight: 44 }}>{action}</button>
      )}
    </div>
  );
}

export function Modal({ title, subtitle, onClose, children, maxWidth = 480, ariaLabel }) {
  const dialogRef = useRef(null);
  // Capture during the mount render, before React commits a consumer's autoFocus control.
  const openerRef = useRef(typeof document !== 'undefined' ? document.activeElement : null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  // A non-string title (e.g. a colored <span> icon+label) with no ariaLabel silently gives the
  // dialog no accessible name at all — loud in dev instead of a silent a11y regression in prod.
  // A real useEffect (not a plain conditional ahead of the other hooks) so it can never become a
  // rules-of-hooks hazard, and only fires once per title/ariaLabel change, not every re-render.
  useEffect(() => {
    if (process.env.NODE_ENV !== 'production' && typeof title !== 'string' && !ariaLabel) {
      console.error('Modal: pass a string `ariaLabel` alongside a non-string `title`, or screen readers get no dialog name.');
    }
  }, [title, ariaLabel]);

  // `aria-modal="true"` tells assistive tech the rest of the page is inert, so focus must not be
  // able to Tab out to it — and must return to the opener on close, by every close path.
  useEffect(() => {
    const opener = openerRef.current;
    const FOCUSABLE = 'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';
    const dialog = dialogRef.current;

    // React applies autoFocus before effects run, so only seed focus when it is still outside.
    // Prefer the first real control over the dismiss button, which sits first in DOM order but is
    // never what a consumer wants focused on open (e.g. an edit form's first field) — fall back to
    // it only if it's the dialog's sole focusable element (a bare alert-style modal).
    if (dialog && !dialog.contains(document.activeElement)) {
      // NOT a comma-separated `${FOCUSABLE}:not(...)` string — :not() there would only bind to the
      // last alternative in the list, not distribute across all of them, and silently match the
      // dismiss button anyway.
      const candidates = dialog.querySelectorAll(FOCUSABLE);
      const first = Array.from(candidates).find(el => !el.hasAttribute('data-modal-dismiss')) || candidates[0];
      if (first) first.focus();
    }

    const onKey = (e) => {
      if (e.key === 'Escape') { onCloseRef.current(); return; }
      if (e.key !== 'Tab') return;
      const currentDialog = dialogRef.current;
      if (!currentDialog) return;
      const items = currentDialog.querySelectorAll(FOCUSABLE);
      if (items.length === 0) return;
      const first = items[0];
      const last = items[items.length - 1];
      const active = document.activeElement;
      if (e.shiftKey) {
        if (active === first || !currentDialog.contains(active)) {
          e.preventDefault();
          last.focus();
        }
      } else if (active === last || !currentDialog.contains(active)) {
        e.preventDefault();
        first.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      // StrictMode simulates effect cleanup while the dialog is still mounted; only restore on a
      // real unmount.
      if (!dialog?.isConnected && opener && typeof opener.focus === 'function') opener.focus();
    };
  }, []);

  return (
    <div
      onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.22)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }}
    >
      <div
        ref={dialogRef}
        role="dialog" aria-modal="true" aria-label={ariaLabel ?? (typeof title === 'string' ? title : undefined)}
        onClick={e => e.stopPropagation()}
        style={{ ...S.card, width: '100%', maxWidth, fontFamily: T.font,
          display: 'flex', flexDirection: 'column',
          maxHeight: 'calc(100vh - 32px)',   // never exceed viewport
        }}
      >
        {/* Header stays fixed — never scrolls away */}
        <div style={{ padding: '28px 28px 0', flexShrink: 0 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 22 }}>
            <div>
              <div style={{ ...S.h2 }}>{title}</div>
              {subtitle && <div style={{ fontSize: 12, color: T.sec, marginTop: 3 }}>{subtitle}</div>}
            </div>
            <button onClick={onClose} data-modal-dismiss aria-label="बंद करें / Close" style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: T.mute, lineHeight: 1, marginLeft: 12, minWidth: 44, minHeight: 44, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>×</button>
          </div>
        </div>
        {/* Scrollable content area */}
        <div style={{ padding: '0 28px 24px', overflowY: 'auto', flex: 1 }}>
          {children}
        </div>
      </div>
    </div>
  );
}
